import React, { useEffect } from 'react';
import { Form, Radio, Checkbox } from 'antd';
import { FormItem, HiddenField } from '@xbcb/form-item-components';
import { shouldUpdate, getCodes } from '@xbcb/ui-utils';
import { useTheme } from 'styled-components';
import { NamePath } from '@xbcb/ui-types';
import DisclaimerSelect from 'components/DisclaimerSelect';
import CpscReferencePgaMessageSet from 'components/CpscReferencePgaMessageSet';
import CpscComponentTabs from 'components/CpscComponentTabs';
import CpscFullPgaMessageSet from 'components/CpscFullPgaMessageSet';
import IntendedUseSelect from 'components/IntendedUseSelect';
import ValidationErrors from 'components/ValidationErrors';
import { useEntryValidation } from 'libs/entryValidation';
import { usePrevious } from 'libs/hooks';
import {
  CpscCertificateType,
  CpscGlobalIdentifierType,
} from '__generated__/graphql';
import {
  StyledCpscCoreFormDiv,
  StyledRootFieldsDiv,
  StyledSelect,
  StyledInput,
  StyledAddonSelect,
  StyledDeclaration,
  StyledDiv,
  StyledFlexDiv,
  StyledTextArea,
} from './styles';
import { isEqual } from 'lodash';

const PROCESSING_TYPES = {
  DISCLAIM: 'DISCLAIM',
  REF: 'REF',
  FULL: 'FULL',
} as const;

const CERTIFICATE_TYPE_OPTIONS = [
  {
    value: CpscCertificateType.CPC,
    label: "CPC - Children's Product Certificate",
  },
  {
    value: CpscCertificateType.GCC,
    label: 'GCC - General Certificate of Conformity',
  },
];

const GLOBAL_IDENTIFIER_TYPE_OPTIONS = [
  {
    value: CpscGlobalIdentifierType.GTIN,
    label: 'GTIN',
    title: 'Global Trade Item Number',
  },
  {
    value: CpscGlobalIdentifierType.UPC,
    label: 'UPC',
    title: 'Universal Product Code',
  },
];

// Props interface for tab content components
interface TabContentProps {
  localNamePath: NamePath;
  fullNamePath: NamePath;
  readOnly?: boolean;
  disabled?: boolean;
}

// Component Map by processing type (module level constant)
const TAB_COMPONENT_MAP: Record<string, React.FC<TabContentProps>> = {
  [PROCESSING_TYPES.REF]: CpscReferencePgaMessageSet,
  [PROCESSING_TYPES.FULL]: CpscFullPgaMessageSet,
};

export interface CpscCoreFormProps {
  fullNamePath: NamePath;
  localNamePath: NamePath;
  readOnly?: boolean;
  required?: boolean;
  disabled?: boolean;
}

const CpscCoreForm: React.FC<CpscCoreFormProps> = ({
  fullNamePath,
  localNamePath,
  readOnly,
  required,
  disabled,
}) => {
  const codes = getCodes();
  const { flag: pgaFlags, disclaimer: disclaimerCodes } = codes.CBP.CATAIR.PGA;
  const form = Form.useFormInstance();
  const theme = useTheme();

  const { errorMessages, validateFields } = useEntryValidation([
    ...fullNamePath,
  ]);
  const previousFields = usePrevious(validateFields);
  useEffect(() => {
    if (validateFields?.length && !isEqual(validateFields, previousFields)) {
      void form.validateFields(validateFields);
    }
  }, [form, previousFields, validateFields]);

  const cpscFlag = pgaFlags.CP1;
  const filterDisclaimerCodes = cpscFlag?.disclaimerCodes;
  const certificateTransmissionPrompt = cpscFlag?.text;
  const allowedCpscIntendedUseCodes =
    codes.CBP.CATAIR.PGA.allowedIntendedUse?.CPS || [];

  const intendedUseCode = Form.useWatch(
    [...fullNamePath, 'intendedUseDetails', 'code'],
    form,
  );
  const isOtherUse = intendedUseCode === '980.000';

  return (
    <StyledCpscCoreFormDiv>
      {errorMessages.length ? (
        <ValidationErrors errors={errorMessages} />
      ) : null}

      <HiddenField
        localNamePath={[...localNamePath, 'programCode']}
        // Currenlty CPSC only has single program. (programCode : CPS)
        value={cpscFlag?.programCode}
        fullNamePath={[...fullNamePath, 'programCode']}
        form={form}
      />
      {/* Intended Use Code Section - using shared IntendedUseSelect component */}
      <StyledFlexDiv>
        <IntendedUseSelect
          form={form}
          localNamePath={[...localNamePath, 'intendedUseDetails', 'code']}
          fullNamePath={[...fullNamePath, 'intendedUseDetails', 'code']}
          codes={allowedCpscIntendedUseCodes}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          hideDescription={isOtherUse}
        />
        {/* Intended Use Description - only shown for 980.000 (For Other Use) */}
        {isOtherUse && (
          <FormItem
            name={[...localNamePath, 'intendedUseDetails', 'description']}
            label="Intended Use Description"
            $itemSize={theme.size.short}
            $readOnly={readOnly}
            rules={[{ required, message: ' ' }]}
          >
            <StyledTextArea
              autoSize
              disabled={disabled}
              maxLength={21}
              placeholder="Enter description"
            />
          </FormItem>
        )}
      </StyledFlexDiv>
      <StyledDiv>
        <p>{certificateTransmissionPrompt}</p>
      </StyledDiv>

      <div className="radio-group-horizontal">
        <FormItem
          name={[...localNamePath, 'processingType']}
          rules={[{ required, message: ' ' }]}
          $readOnly={readOnly}
          $itemSize={theme.size.short}
          initialValue={PROCESSING_TYPES.DISCLAIM}
        >
          <Radio.Group disabled={disabled || readOnly}>
            <Radio value={PROCESSING_TYPES.DISCLAIM}>Disclaim</Radio>
            <Radio value={PROCESSING_TYPES.REF}>
              Reference PGA message set
            </Radio>
            <Radio value={PROCESSING_TYPES.FULL}>Full PGA message set</Radio>
          </Radio.Group>
        </FormItem>
      </div>

      {/* Conditional content based on processing type */}
      <Form.Item
        shouldUpdate={shouldUpdate([[...fullNamePath, 'processingType']])}
        noStyle
      >
        {() => {
          const processingType = form.getFieldValue([
            ...fullNamePath,
            'processingType',
          ]);
          const TabContentComponent = TAB_COMPONENT_MAP[processingType];
          const showRootFields = processingType === PROCESSING_TYPES.FULL;

          return (
            <>
              {/* Certificate Type and Global Identifier - shown for FULL only */}
              {showRootFields && (
                <StyledRootFieldsDiv>
                  <FormItem
                    name={[...localNamePath, 'certificateType']}
                    label="Certificate type"
                    $itemSize={theme.size.short_medium}
                    $readOnly={readOnly}
                    rules={[{ required: true }]}
                    validateTrigger="onBlur"
                  >
                    <StyledSelect
                      options={CERTIFICATE_TYPE_OPTIONS}
                      disabled={disabled || readOnly}
                      placeholder="Select certificate type"
                    />
                  </FormItem>
                  <FormItem
                    name={[...localNamePath, 'globalIdentifier', 'code']}
                    label="Global Identifier (optional)"
                    $itemSize={theme.size.short_medium}
                    $readOnly={readOnly}
                  >
                    <StyledInput
                      addonBefore={
                        <Form.Item
                          name={[...localNamePath, 'globalIdentifier', 'type']}
                          noStyle
                        >
                          <StyledAddonSelect
                            options={GLOBAL_IDENTIFIER_TYPE_OPTIONS}
                            disabled={disabled || readOnly}
                            placeholder="Type"
                            allowClear
                          />
                        </Form.Item>
                      }
                      maxLength={19}
                      disabled={disabled || readOnly}
                      placeholder="Enter code"
                    />
                  </FormItem>
                </StyledRootFieldsDiv>
              )}

              {/* Disclaim reason - shown for DISCLAIM only */}
              {processingType === PROCESSING_TYPES.DISCLAIM && (
                <DisclaimerSelect
                  localNamePath={[...localNamePath, 'disclaimerCode']}
                  label="Disclaim reason"
                  disclaimerCodes={disclaimerCodes}
                  filter={filterDisclaimerCodes}
                  required={required}
                  readOnly={readOnly}
                  disabled={disabled}
                />
              )}

              {/* Component Tabs - shown for REF and FULL */}
              {TabContentComponent && (
                <FormItem
                  name={[...localNamePath, 'items']}
                  initialValue={[{}]}
                >
                  <CpscComponentTabs
                    readOnly={readOnly}
                    disabled={disabled}
                    renderTabContent={(index) => (
                      <TabContentComponent
                        localNamePath={[...localNamePath, 'items', index]}
                        fullNamePath={[...fullNamePath, 'items', index]}
                        readOnly={readOnly}
                        disabled={disabled}
                      />
                    )}
                  />
                </FormItem>
              )}

              {/* Certificate Declaration - shown for FULL only */}
              {showRootFields && (
                <StyledDeclaration data-testid="certificate-declaration">
                  <FormItem
                    name={[
                      ...localNamePath,
                      'certificateDeclaration',
                      'certified',
                    ]}
                    valuePropName="checked"
                    initialValue={true}
                  >
                    <Checkbox disabled={disabled || readOnly}>
                      <span className="declaration-text">
                        The certifying entity certifies that the finished
                        product(s) covered by this certificate comply with the
                        rules, bans, standards, and regulations stated herein,
                        and that the information in this certificate is true and
                        accurate to the best of my knowledge, information, and
                        belief. The certifying entity understands and
                        acknowledges that it is a United States federal crime to
                        knowingly and willfully make any materially false,
                        fictitious, or fraudulent statements, representations,
                        or omissions, on this certificate.
                      </span>
                    </Checkbox>
                  </FormItem>
                </StyledDeclaration>
              )}
            </>
          );
        }}
      </Form.Item>
    </StyledCpscCoreFormDiv>
  );
};

export default CpscCoreForm;
